import { SEARCH_LIST_PAGE_SIZE } from '@/constants/searchList';
import { normalizeDeliveryFilters } from './deliveryFilters';

export const SEARCH_LIST_PATH = '/page/list';

const toNumber = (value: string | null) => {
  if (value == null || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const appendIfPresent = (sp: URLSearchParams, key: string, value: unknown) => {
  if (value == null || value === '') {
    return;
  }
  sp.set(key, String(value));
};

export function buildSearchListQueryString(
  params: Record<string, any> = {},
  selectParams: Record<string, any> = {},
): string {
  const sp = new URLSearchParams();

  if (params.key) {
    sp.set('key', params.key);
  }
  if (params.cat) {
    sp.set('cat', String(params.cat));
  }
  if (selectParams.cataIdList) {
    sp.set('cataIdList', String(selectParams.cataIdList));
  }
  if (params.pageNo && Number(params.pageNo) !== 1) {
    sp.set('pageNo', String(params.pageNo));
  }
  if (params.sort != null && String(params.sort) !== '0') {
    sp.set('sort', String(params.sort));
  }
  if (params.stockFlag === 2) {
    sp.set('stockFlag', '2');
  }
  // URL 使用逗号分隔编码，刷新及浏览器前进后退时恢复多选条件。
  const promiseLabels = normalizeDeliveryFilters(params.promiseLabels);
  if (promiseLabels.length) sp.set('promiseLabels', promiseLabels.join(','));
  if (Array.isArray(params.featureTags) && params.featureTags.length) {
    sp.set('featureTags', params.featureTags.join(','));
  }
  if (Array.isArray(params.brandRole) && params.brandRole.length) {
    sp.set('brandRole', params.brandRole.join(','));
  }
  appendIfPresent(sp, 'priceMin', params.priceMinFilter);
  appendIfPresent(sp, 'priceMax', params.priceMaxFilter);

  if (Array.isArray(params.extAttrList) && params.extAttrList.length) {
    const compact = params.extAttrList.map((item: Record<string, string>) => {
      const expandSortId = Object.keys(item)[0];
      const valueId = item[expandSortId];
      const meta = (selectParams.extAttrList || []).find((entry: Record<string, any>) => entry?.[valueId]);
      const label = meta?.[valueId];
      return {
        expandSortId,
        valueId,
        name: label?.name,
        groupName: label?.key,
      };
    });
    sp.set('extAttrList', JSON.stringify(compact));
  }

  const query = sp.toString();
  return query ? `?${query}` : '';
}

export function buildSearchListHistoryLocation(
  params: Record<string, any> = {},
  selectParams: Record<string, any> = {},
  options?: { query?: string; searchTriggerTs?: number },
) {
  return {
    pathname: SEARCH_LIST_PATH,
    search: buildSearchListQueryString(params, selectParams),
    query: options?.query ?? params.key ?? '',
    cat: params.cat || undefined,
    cataIdList: selectParams.cataIdList || undefined,
    searchTriggerTs: options?.searchTriggerTs,
  };
}

export function parseSearchListLocation(location: any = {}) {
  const rawSearch = location?.search || '';
  const search = rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch;
  const sp = new URLSearchParams(search);

  const keyFromUrl = sp.get('key') || '';
  const keyFromState = location?.query || '';
  const catFromUrl = sp.get('cat') || '';
  const catFromState = location?.cat || '';
  const cataIdList = sp.get('cataIdList') || location?.cataIdList || '';

  const cat = catFromUrl || catFromState || '';
  const key = cat ? '' : keyFromUrl || keyFromState || '';

  const apiParams: Record<string, any> = {
    key,
    cat,
    pageNo: toNumber(sp.get('pageNo')) || 1,
    sort: toNumber(sp.get('sort')) || 0,
    pageSize: SEARCH_LIST_PAGE_SIZE,
  };

  if (sp.get('stockFlag') === '2') {
    apiParams.stockFlag = 2;
  }

  const promiseLabels = sp.get('promiseLabels');
  if (promiseLabels) {
    apiParams.promiseLabels = normalizeDeliveryFilters(promiseLabels.split(',').map(Number));
  }

  const featureTags = sp.get('featureTags');
  if (featureTags) {
    apiParams.featureTags = featureTags.split(',').filter(Boolean);
  }

  const brandRole = sp.get('brandRole');
  if (brandRole) {
    apiParams.brandRole = brandRole.split(',').filter(Boolean);
  }

  const priceMin = toNumber(sp.get('priceMin'));
  const priceMax = toNumber(sp.get('priceMax'));
  if (priceMin != null) {
    apiParams.priceMinFilter = priceMin;
  }
  if (priceMax != null) {
    apiParams.priceMaxFilter = priceMax;
  }

  const selectParams: Record<string, any> = {};
  if (cataIdList) {
    selectParams.cataIdList = cataIdList;
    selectParams.cat = cat;
  }

  const extAttrRaw = sp.get('extAttrList');
  if (extAttrRaw) {
    try {
      const parsed = JSON.parse(extAttrRaw);
      if (Array.isArray(parsed)) {
        apiParams.extAttrList = parsed.map((item: any) => ({
          [item.expandSortId]: item.valueId,
        }));
        selectParams.extAttrList = parsed.map((item: any) => ({
          [item.valueId]: {
            key: item.groupName || item.expandSortId,
            name: item.name || item.valueId,
          },
        }));
        selectParams.selectId = parsed.map((item: any) => item.valueId);
      }
    } catch (error) {
      console.error('parse extAttrList from url failed', error);
    }
  }

  return { apiParams, selectParams };
}

export function getSearchListLocationSignature(location: any = {}) {
  const { search = '', query = '', cat = '', cataIdList = '', searchTriggerTs = '' } = location;
  return [search, query, cat, cataIdList, searchTriggerTs].join('|');
}
